import React, { Suspense } from 'react';
import { Dialog, DialogContent, IconButton, Box, Typography, Breadcrumbs, useMediaQuery, useTheme } from '@mui/material';
import { Close, ArrowBack, NavigateNext } from '@mui/icons-material';
import { useRecordDialog } from '../contexts/RecordDialogContext';

// Each entity type maps to its detail component. These are the same components
// used by the (legacy) full-page routes; they accept an optional `id` prop and
// `dialogContext`/`onClose` props so they render correctly in either context.
// They are lazy-loaded so the initial bundle does not change.
const contentLoaders = {
  client:     () => import('../pages/ClientDetail'),
  contact:    () => import('../pages/ContactDetail'),
  asset:      () => import('../pages/AssetDetail'),
  site:       () => import('../pages/SiteDetail'),
  ticket:     () => import('../pages/TicketDetail'),
  project:    () => import('../pages/ProjectDetail'),
  license:    () => import('../pages/RenewalDetail'),
  quote:      () => import('../pages/QuoteDetail'),
  invoice:    () => import('../pages/InvoiceDetail'),
  credential: () => import('../pages/records/CredentialViewContent'),
  kb:         () => import('../pages/records/KbViewContent'),
  clientCredits: () => import('../pages/ClientCredits')
};

const typeLabels = {
  client: 'Client',
  contact: 'Contact',
  asset: 'Asset',
  site: 'Site',
  ticket: 'Ticket',
  project: 'Project',
  license: 'License',
  quote: 'Quote',
  invoice: 'Invoice',
  credential: 'Credential',
  kb: 'KB Article',
  clientCredits: 'Credit History'
};

const entryLabel = (entry) => entry.props?.title || typeLabels[entry.type] || entry.type;

const lazyRegistry = {};
const getLazyComponent = (type) => {
  const loader = contentLoaders[type];
  if (!loader) return null;
  if (!lazyRegistry[type]) {
    lazyRegistry[type] = React.lazy(loader);
  }
  return lazyRegistry[type];
};

const LoadingFallback = () => (
  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
    <Typography sx={{ fontSize: '0.875rem' }}>Loading...</Typography>
  </Box>
);

const sizeForType = {
  credential: 'sm',
  contact: 'md',
  site: 'md',
  asset: 'lg',
  license: 'lg',
  ticket: 'lg',
  project: 'lg',
  quote: 'lg',
  invoice: 'lg',
  client: 'lg',
  kb: 'md',
  clientCredits: 'lg'
};

/**
 * Single-overlay record host. The record stack from RecordDialogContext is
 * rendered as ONE dialog showing the top entry, with a breadcrumb trail for
 * the entries beneath it. Navigating to a related record swaps the content
 * in place (no dialog-on-dialog stacking); the back arrow / Esc pops one
 * level, the X (and backdrop click) closes the whole trail.
 */
const RecordDialogHost = () => {
  const { stack, closeRecord, closeAll } = useRecordDialog();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  if (!stack.length) return null;

  const top = stack[stack.length - 1];
  const Component = getLazyComponent(top.type);
  const maxWidth = top.props?.maxWidth || sizeForType[top.type] || 'md';
  const hasTrail = stack.length > 1;

  // Legacy full-page detail components (AssetDetail, ClientDetail, etc.) used
  // to get their padding from a parent <Container> on their /route. When
  // rendered inside a dialog they have no outer container, so the host has
  // to provide breathing room. CredentialViewContent and KbViewContent
  // self-pad, so we leave them as-is.
  const selfPadded = top.type === 'credential' || top.type === 'kb';
  const contentSx = selfPadded
    ? { p: 0 }
    : {
        px: fullScreen ? 2 : 3,
        pt: hasTrail ? 1 : (fullScreen ? 2 : 3),
        pb: fullScreen ? 2 : 3,
        pr: fullScreen ? 2 : 6
      };

  return (
    <Dialog
      open
      fullScreen={fullScreen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          if (top.props?.disableBackdropClose) return;
          closeAll();
          return;
        }
        // Esc pops one level, like the back arrow.
        closeRecord();
      }}
      maxWidth={maxWidth}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: fullScreen ? '100%' : '50vh'
        }
      }}
      keepMounted={false}
    >
      <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}>
        <IconButton size={fullScreen ? 'medium' : 'small'} onClick={closeAll} aria-label="Close record">
          <Close fontSize={fullScreen ? 'medium' : 'small'} />
        </IconButton>
      </Box>
      {hasTrail && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: fullScreen ? 1 : 2, pt: 1.5 }}>
          <IconButton size="small" onClick={() => closeRecord()} aria-label="Back to previous record">
            <ArrowBack fontSize="small" />
          </IconButton>
          <Breadcrumbs separator={<NavigateNext sx={{ fontSize: 14 }} />} sx={{ '& .MuiBreadcrumbs-li': { display: 'flex' } }}>
            {stack.map((entry, idx) => (
              <Typography
                key={entry.key}
                variant="body2"
                sx={{
                  color: idx === stack.length - 1 ? 'text.primary' : 'text.secondary',
                  fontWeight: idx === stack.length - 1 ? 600 : 400
                }}
              >
                {entryLabel(entry)}
              </Typography>
            ))}
          </Breadcrumbs>
        </Box>
      )}
      <DialogContent sx={contentSx}>
        <Suspense fallback={<LoadingFallback />}>
          {Component ? (
            <Component
              key={top.key}
              id={top.id}
              mode={top.mode}
              dialogContext
              entryProps={top.props}
              onClose={() => closeRecord(top.key)}
            />
          ) : (
            <Box sx={{ p: 4 }}>
              <Typography color="error">Unknown record type: {top.type}</Typography>
            </Box>
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
};

export default RecordDialogHost;
